import './app.css'
import './tailwind.css'

import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import { SnackbarProvider } from 'notistack'

import { registerAdminAuthFailureHandler } from '@gto/api-client'
import { IS_LOCAL_ENV } from '@gto/helpers/env-checkers'

import { AdminSnackbarMessage } from '@/components/admin-snackbar-message'

import { queryClient } from './query-client'
import { createRouter } from './router'

const router = createRouter()

// Finish the logout the DOAT client can only start. It clears the credentials
// itself; these are the two steps that need the app's router and query client.
// Same shape as the header's Logout button — purge the cache so a later sign-in
// in this tab can't flash the previous staff member's data, then bounce to
// /login — with one deliberate difference: an involuntary logout keeps the deep
// link, which the header button has no reason to. Without this the session is
// gone but the page still looks authenticated, which is the "a 401 never logs
// you out" half of C-1820.
//
// `router.state.location.href` is the router-relative href (`/announcements?x=1`),
// which is what `/login`'s `from` param validates against; `window.location.href`
// is absolute and SAFE_PATH would reject it, silently dropping the deep link.
// `reason` is what makes the bounce legible: without it this navigation is
// indistinguishable from arriving unauthenticated, so a staff member whose
// account the admin API rejects just signs in again and loops (C-1835).
registerAdminAuthFailureHandler(() => {
  queryClient.clear()
  void router.navigate({
    to: '/login',
    search: { from: router.state.location.href, reason: 'session_ended' },
  })
})

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider
        Components={{
          warning: AdminSnackbarMessage,
          error: AdminSnackbarMessage,
          info: AdminSnackbarMessage,
          success: AdminSnackbarMessage,
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      />
      <RouterProvider router={router} />
      {IS_LOCAL_ENV() && <ReactQueryDevtools />}
    </QueryClientProvider>
  )
}
